package link.erpango.quip.core.factory

import androidx.compose.runtime.Composable
import androidx.lifecycle.ViewModelStoreOwner
import androidx.lifecycle.viewmodel.compose.viewModel
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.data.remote.repository.catalog.ICatalogRepository
import link.erpango.quip.data.remote.repository.user.IUserRepository
import link.erpango.quip.domain.catalog.DefaultSparePartCatalog
import link.erpango.quip.domain.entities.DSparePartCatalog
import link.erpango.quip.domain.entities.DVehicleBrand
import link.erpango.quip.domain.usecases.IMySparePartsUseCase
import link.erpango.quip.features.myspareparts.MySparePartEditViewModel
import link.erpango.quip.features.myspareparts.MySparePartsViewModel
import link.erpango.quip.features.myspareparts.ShareMySparePartViewModel
import link.erpango.quip.features.myspareparts.toDomainVehicleBrands

class MySparePartsFactory(
    private val mySparePartsInteractor: IMySparePartsUseCase,
    private val catalogRepository: ICatalogRepository,
    private val userRepository: IUserRepository,
    private val viewModelStoreOwner: ViewModelStoreOwner
) : IFactory {

    /** Catálogo de categorías/tipos para los formularios (fijo hasta que el backend lo exponga). */
    val sparePartCatalog: DSparePartCatalog = DefaultSparePartCatalog

    @Composable
    fun createMySparePartsViewModel(): MySparePartsViewModel {
        return viewModel(viewModelStoreOwner) {
            MySparePartsViewModel(mySparePartsInteractor)
        }
    }

    /**
     * ViewModel de la sección "Repuestos" del detalle de vehículo (una instancia por VIN).
     * Carga solo los repuestos enlazados a ese vehículo.
     */
    @Composable
    fun createVehicleMySparePartsViewModel(vehicleVin: String): MySparePartsViewModel {
        return viewModel(viewModelStoreOwner, "my_spare_parts_vehicle_$vehicleVin") {
            MySparePartsViewModel(mySparePartsInteractor, initialVehicleVin = vehicleVin)
        }
    }

    /**
     * ViewModel de la pantalla crear/editar repuesto personal.
     * `partId == null` → modo creación; con ID → modo edición (una instancia por ID).
     * [preselectVin] (solo creación) preselecciona un vehículo del garaje; cada VIN tiene
     * su propia instancia para no mezclar borradores entre orígenes.
     */
    @Composable
    fun createMySparePartEditViewModel(partId: Long? = null, preselectVin: String? = null): MySparePartEditViewModel {
        val key = when {
            partId != null -> "my_spare_part_edit_$partId"
            preselectVin != null -> "my_spare_part_edit_new_$preselectVin"
            else -> "my_spare_part_edit_new"
        }
        return viewModel(viewModelStoreOwner, key) {
            MySparePartEditViewModel(
                mySparePartsUseCase = mySparePartsInteractor,
                catalogRepository = catalogRepository,
                sparePartCatalog = sparePartCatalog,
                partId = partId,
                preselectVin = preselectVin
            )
        }
    }

    /** Catálogo de marcas/modelos para resolver compatibilidades; vacío si no está disponible. */
    suspend fun loadVehicleBrands(): List<DVehicleBrand> {
        return when (val result = catalogRepository.getCatalog()) {
            is DResponse.Success -> result.data.toDomainVehicleBrands()
            else -> emptyList()
        }
    }

    /**
     * ViewModel de la pantalla de compartir un repuesto personal (una instancia por ID).
     * [currentUserId] es el usuario con sesión activa; se excluye de la lista de destinos.
     */
    @Composable
    fun createShareMySparePartViewModel(partId: Long, currentUserId: String? = null): ShareMySparePartViewModel {
        return viewModel(viewModelStoreOwner, "my_spare_part_share_$partId") {
            ShareMySparePartViewModel(
                mySparePartsUseCase = mySparePartsInteractor,
                userRepository = userRepository,
                partId = partId,
                currentUserId = currentUserId
            )
        }
    }
}
